<template>
    <div id="building-pharmacy-location-container">
        <h4 class="page-title text-truncate">{{$t('PharmacyBuildingLocation.EditTitle')}}</h4>

        <div class="page-height">
            <div class="alert alert-fill-warning d-flex align-items-center m-3" role="alert" v-if="isReadOnly">
                <i class="mdi mdi-alert-circle me-2" style="font-size: 1.5rem;"></i>
                <div class="flex-grow-1">
                    <span>{{ $t('Shared.ReadOnlyPermission') }}</span>
                </div>
            </div>

            <b-row>
                <b-col lg="3" md="3" sm="12">
                    <MdEdit 
                        v-bind:Label="$t('Shared.Code')"
                        v-bind:Name="'Code'"
                        v-bind:TypeProp="'text'"  
                        v-bind:MaxLengthProp="10"     
                        v-model="editableItem.code"
                        v-bind:isReadOnly="true"
                        v-bind:Id="'input_code'"
                        v-bind:isRequired="false"
                    />
                </b-col>
                <b-col lg="6" md="6" sm="12">
                    <MdEdit 
                        v-bind:Label="$t('Shared.Description')"
                        v-bind:Name="'Description'"
                        v-bind:TypeProp="'text'"  
                        v-bind:MaxLengthProp="50"         
                        v-model="editableItem.name"
                        v-bind:isReadOnly="true"
                        v-bind:Id="'input_description'"
                        v-bind:isRequired="false"
                    /> 
                </b-col>
            </b-row>

            <MdTitle v-bind:Text="$t('PharmacyBuildingLocation.Pharmacy')" v-bind:isSubtitle="true" v-bind:Name="'T_Notes'"/>
            <b-row>
                <b-col>
                    <MdCheckListBox2 
                        v-model="selected"
                        v-bind:Options="PharmacyLocation"
                        v-bind:OnChangeProp="OnChangeProp"
                        v-bind:columnCount="6"
                        v-bind:UseClassFlex="true"
                        :textField="'name'"
                        :valueField="'code'"
                        :isReadOnly="isReadOnly || loading"/>
                </b-col>
            </b-row>
        </div>

        <div class="space-actions"></div>        
        <div class="page-actions">
            <MdButton
                v-bind:Label="$t('Shared.Back')"
                v-bind:Name="'btn_pharmacy_location_list'"
                v-bind:ClassProp="'secondary mx-2 btn-login'"
                v-on:click="discardChanges"/>
        
            <MdButton
                v-bind:Label="$t('Shared.Save')"
                v-bind:ClassProp="isReadOnly ? 'primary mx-2':'primary btn-login mx-2'"
                v-on:click="saveOrEdit"
                v-bind:isReadOnly="!saveButtonEnabled"
                v-bind:Name="'btn_save_pharmacy_locations'"/>
        </div>
    </div>
</template>

<script>
import ShowMessage from '@/components/messages/ShowMessage.js';
import DBFunctions from '@/common/DBFunctions.js';
import BuildingService from '@/services/building.service'
import ePharmacyLocationsService from '@/services/epharmacylocation.service';
import { EventBus } from '@/event-bus.js';

export default {
    name: 'BuildingPharmacyLocationSetup',
    mixins: [ShowMessage, DBFunctions],

    data() {
        return {
            isReadOnly: false,
            editableItem: {
                code: null,
                location: null,
            },
            pendingChanges: false,
            PharmacyLocation: [],
            selected: [],
            loading: true,
            OffRoute: false,
            WasSaved: false,
        }
    },

    computed: {
        saveButtonEnabled() {
            return !this.isReadOnly && this.pendingChanges && !this.loading;
        }
    },

    methods: {
        discardChanges() {
            this.$router.push({ name: "BuildingPharmacyLocationSetupList"});
        },

        OnChangeProp() {

        },

        async saveOrEdit() {
            await BuildingService
                .updatePharmacies(this.$route.params.id, {pharmacies: this.selected})
                .then((response) => {
                    if(response.status == '200'){                
                        this.WasSaved = true;
                        this.pendingChanges = false;

                        if(!this.OffRoute) {
                            this.discardChanges();
                        }

                        setTimeout(() => 
                            this.$bvToast.toast(this.$t('Msg.SavedSuccessfully'), {
                                title: this.$t('Msg.Success'),
                                autoHideDelay: 5000,
                                variant: "success"
                            }
                        ), 50);
                    }
                })
                .catch((error) => {
                    this.OffRoute = false;
                    this.showResponseErrorMessage(error);
                });
        },
        
        getBuildingLocation() {
            BuildingService
                .Get(this.$route.params.id)
                .then((response) => {
                    if(response.status == '200' && response.data){                
                        this.editableItem = response.data;
                    }
                })
                .catch((error) => {
                    this.showResponseErrorMessage(error);
                });
        },

        getBuildingPharmacies() {
            BuildingService
                .getPharmacies(this.$route.params.id)
                .then((response) => {
                    if(response.status == '200' && response.data){                
                        this.selected = response.data;
                    }
                })
                .catch((error) => {
                    this.showResponseErrorMessage(error);
                });
        },

        async onLoad() {
            await ePharmacyLocationsService
                .getAll({PageSize: 0, PageNumber: 0})
                .then((response) => {
                    if(response.status == '200' && response.data){  
                        this.PharmacyLocation = response.data;   
                    }
                })
                .catch((error) => {
                    this.showResponseErrorMessage(error);
                });
        },

        async ClickOffRoute() {
            this.OffRoute = true;       
            await this.saveOrEdit();
        },

        beforeWindowUnload(e) {
            if (this.pendingChanges) {
                e.preventDefault()
                e.returnValue = ''	
            }
        },
    },

    async mounted() {
        await this.onLoad();

        if(this.$attrs.editMode){
            Promise.allSettled([this.getBuildingLocation(), this.getBuildingPharmacies()])
                .then((result) => result.length == 2 ?
                        setTimeout(() =>
                            this.loading = false
                        , 100) : null
                )
                .catch((error) => {
                    this.loading = false;
                    this.showResponseErrorMessage(error);
                });
        }
    },

    created(){
        EventBus.$on("onChanged", function (e) { 
            if (!this.loading) {      
                this.pendingChanges |= e.Changed
            }
        }.bind(this));
        window.addEventListener('beforeunload', this.beforeWindowUnload)
    },

    beforeDestroy () {
        EventBus.$off("onChanged");
        window.removeEventListener('beforeunload', this.beforeWindowUnload)
    },

    beforeRouteLeave(to, from, next) {
        if (!this.isReadOnly && this.pendingChanges) {
            this.$bvModal.msgBoxConfirm(this.$t('Msg.LeavingWithoutSaving'), {
                            title: this.$t('Msg.Confirm'),
                            okTitle: this.$t('Shared.BtnSave'),
                            cancelTitle: this.$t('Shared.BtnDismiss'),
                            footerClass: 'p-2',
                            titleClass: 'modal-title modal-type-warning'
                        })
                        .then(async value => {
                            if(!value){
                                next();
                            } else {
                                await this.ClickOffRoute();
                                var f = this.WasSaved;             
                                if(f){
                                    next();
                                }
                            }
                        })		
        } else {
            next()
        }
    },
}
</script>